import { Microphone01 } from "@untitledui/icons"

import { cx } from "@/components/ui/cx"
import { mmss } from "@/lib/duration"

import { MEMO_GUIDE } from "./copy"

/*
 * The memo guide: the one screen read at a distance, phone held away from the
 * ear after the tone. It is the board (print.900) so it reads in a corridor;
 * headline type is condensed and clears the contrast floor at arm's length.
 * The audio prompt is fixed; the screen does the structuring. No buttons but
 * Cancel memo — hanging up is the commit, exactly as the prompt says.
 */
export function MemoGuide({
  recording,
  seconds,
  maxSeconds,
  openItems,
  glossary,
  cancelLabel,
  cancelDisabled,
  onCancel,
}: {
  /** False while the prompt is still playing, true from the tone. */
  recording: boolean
  seconds: number | null
  maxSeconds: number
  openItems: string[]
  glossary: string[]
  cancelLabel: string
  cancelDisabled: boolean
  onCancel: () => void
}) {
  return (
    <div className="board flex flex-col gap-5 rounded-xl p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span
          className={cx(
            "tracking-chip text-2xs inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 font-mono font-medium",
            recording
              ? "text-ink-950 bg-stone-50"
              : "border border-dashed border-stone-50 text-stone-50",
          )}
        >
          <Microphone01 aria-hidden="true" className="size-3.5" />
          {recording ? "Recording" : "The prompt is playing"}
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-display-xs text-primary_on-brand font-mono tabular-nums">
            {recording ? mmss(seconds ?? 0) : "0:00"}
          </span>
          <span className="text-quaternary_on-brand font-mono text-xs">
            max {mmss(maxSeconds)}
          </span>
        </span>
      </div>

      <Prompt head={MEMO_GUIDE.sayWhat}>
        {openItems.length > 0 ? (
          openItems.map((label) => <Line key={label}>{label}</Line>)
        ) : (
          <Line quiet>
            Everything on the agenda is marked. Say what else happened.
          </Line>
        )}
      </Prompt>

      <Prompt head={MEMO_GUIDE.promised}>
        <Line quiet>{MEMO_GUIDE.promisedHint}</Line>
      </Prompt>

      {glossary.length > 0 && (
        <Prompt head={MEMO_GUIDE.slowly}>
          <Line>{glossary.join("  ·  ")}</Line>
        </Prompt>
      )}

      <div className="border-onprint-rule flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <span className="text-secondary_on-brand text-sm">
          {MEMO_GUIDE.close}
        </span>
        <button
          type="button"
          disabled={cancelDisabled}
          onClick={onCancel}
          className="h-9 rounded-lg border border-stone-50 px-3.5 text-sm font-medium text-stone-50 disabled:opacity-60 pointer-coarse:h-11"
        >
          {cancelLabel}
        </button>
      </div>
    </div>
  )
}

function Prompt({
  head,
  children,
}: {
  head: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="eyebrow text-quaternary_on-brand">{head}</span>
      <ul className="flex flex-col gap-1">{children}</ul>
    </div>
  )
}

function Line({
  quiet = false,
  children,
}: {
  quiet?: boolean
  children: React.ReactNode
}) {
  return (
    <li
      className={cx(
        "font-condensed leading-tight font-semibold",
        quiet
          ? "text-secondary_on-brand text-md"
          : "text-primary_on-brand text-xl",
      )}
    >
      {children}
    </li>
  )
}
